import { useState } from "react";
import { useNavigate } from "react-router";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatApiError } from "@/lib/format-api-error";
import { useCreateJourney } from "./use-create-journey";

// Phase 7 sub-bucket 14 — replaces /app/admin/journeys/_components/NewJourneyDialog.tsx.
// Two variants: default (compact header CTA) and empty (full button inside the
// EmptyState). execution_order defaults to "flexible" server-side; the dialog
// doesn't expose it (matches legacy parity — admins can change it later from
// the builder header if needed).
//
// On success: close + reset, then navigate to /admin/journeys/:id so the
// admin lands on the builder with the tasks tab open.

export function CreateJourneyDialog({
  variant = "default",
}: {
  variant?: "default" | "empty";
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const mutation = useCreateJourney();

  function reset() {
    setName("");
    setDescription("");
  }

  function onOpenChange(next: boolean) {
    if (!next) reset();
    setOpen(next);
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;
    mutation.mutate(
      {
        name: trimmedName,
        description: description.trim() ? description.trim() : null,
        execution_order: "flexible",
      },
      {
        onSuccess: (res) => {
          setOpen(false);
          reset();
          toast.success("Journey created");
          navigate(`/admin/journeys/${res.id}`);
        },
        onError: (err) => {
          toast.error(formatApiError(err, "Failed to create journey"));
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        {variant === "empty" ? (
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Create your first journey
          </Button>
        ) : (
          <Button size="sm">
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            New journey
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            New journey
          </DialogTitle>
          <DialogDescription>
            Give the journey a name and a short description. You can add tasks
            and assign partners on the next screen.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="j-name">Name</Label>
            <Input
              id="j-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Partner Onboarding"
              required
              maxLength={200}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="j-description">Description (optional)</Label>
            <Textarea
              id="j-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short note shown to partners and admins."
              rows={3}
              maxLength={500}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending || !name.trim()}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create journey
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
